/**
 * @module TaskDetailPanel
 * @description Reusable Task Detail Slide-Over panel extracted from MeineAufgaben.
 *   Shows full task details, edit form, watchers, attachments, and chatter.
 * @since v2.323.2
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import UniversalChatter from './UniversalChatter';
import ConfirmDialog from './ConfirmDialog';
import {
  X, Trash2, Clock, CheckCircle2, Plus, MessageSquare, Bot, Calendar,
  AlertCircle, Eye, Edit3, Link as LinkIcon, Paperclip, Download, Image,
  FileText, File, Users, Send as SendIcon,
} from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';
const f = (url, opts = {}) => fetch(`${API}${url}`, { credentials: 'include', ...opts });

// ─── Helpers ────────────────────────────────────────────────────────────────
const todayISO = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const PRIO_LABEL = (t) => ({
  urgent: t('meineAufgaben.prio.urgent', 'Dringend'),
  high:   t('meineAufgaben.prio.high', 'Hoch'),
  normal: t('meineAufgaben.prio.normal', 'Normal'),
  low:    t('meineAufgaben.prio.low', 'Niedrig'),
});

const KAT_LABEL = (t) => ({
  deep_work: t('meineAufgaben.kat.deepWork', 'Deep Work'),
  meeting:   t('meineAufgaben.kat.meeting', 'Meeting'),
  admin:     t('meineAufgaben.kat.admin', 'Admin'),
  sales:     t('meineAufgaben.kat.sales', 'Sales'),
  hr:        t('meineAufgaben.kat.hr', 'HR'),
  ops:       t('meineAufgaben.kat.ops', 'Ops'),
  followup:  t('meineAufgaben.kat.followup', 'Follow-up'),
});

const SOURCE_BADGE = {
  manual:    { label: 'Manuell',  color: '#6B7280', bg: '#F3F4F6', icon: Plus },
  chatter:   { label: 'Chatter',  color: '#338edc', bg: '#E0F2FE', icon: MessageSquare },
  system:    { label: 'System',   color: '#7C3AED', bg: '#EDE9FE', icon: Bot },
  calendly:  { label: 'Calendly', color: '#0891B2', bg: '#CFFAFE', icon: Calendar },
  calendar:  { label: 'Kalender', color: '#0891B2', bg: '#CFFAFE', icon: Calendar },
  onboarding:{ label: 'Onboarding', color: '#059669', bg: '#D1FAE5', icon: CheckCircle2 },
  case:      { label: 'Case',     color: '#DC2626', bg: '#FEE2E2', icon: AlertCircle },
};

const ENTITY_PATH = {
  kunde:        (id) => `/kunden/${id}`,
  case:         (id) => `/cases/${id}`,
  bewerber:     (id) => `/recruiting/bewerber/${id}`,
  mitarbeiter:  (id) => `/mitarbeiter/${id}`,
  lead:         (id) => `/sales/leads/${id}`,
  forecast:     ()   => `/forecast`,
};

const STATUS_LABELS = (t) => ({
  offen:          t('meineAufgaben.status.open', 'Offen'),
  in_bearbeitung: t('meineAufgaben.status.inProgress', 'In Bearbeitung'),
  erledigt:       t('meineAufgaben.status.done', 'Erledigt'),
  verschoben:     t('meineAufgaben.status.postponed', 'Verschoben'),
});

const PRIO_BORDER = {
  urgent: 'border-l-red-500',
  high:   'border-l-amber-500',
  normal: 'border-l-transparent',
  low:    'border-l-gray-300',
};

function formatDueLabel(date, t) {
  if (!date) return null;
  const d = new Date(typeof date === 'string' && date.length === 10 ? date + 'T00:00:00' : date);
  if (isNaN(d.getTime())) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  if (diff < 0)  return t('meineAufgaben.overdueDays', '{{n}} Tage ueberfaellig').replace('{{n}}', String(Math.abs(diff)));
  if (diff === 0) return t('meineAufgaben.today', 'Heute');
  if (diff === 1) return t('meineAufgaben.tomorrow', 'Morgen');
  if (diff <= 7) return t('meineAufgaben.inDays', 'In {{n}} Tagen').replace('{{n}}', String(diff));
  return d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit' });
}

function initials(name) {
  if (!name) return '?';
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || '?';
}

function Avatar({ name, size = 28 }) {
  return (
    <div
      style={{
        width: size, height: size, borderRadius: '50%',
        background: 'linear-gradient(135deg, #338edc, #6366F1)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, color: '#fff', fontSize: Math.max(10, Math.round(size * 0.38)), fontWeight: 700,
      }}
    >
      {initials(name)}
    </div>
  );
}

function MetaItem({ label, value, danger }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider mb-0.5" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{label}</p>
      <p className={`text-sm font-medium ${danger ? 'text-red-500' : ''}`} style={{ color: danger ? undefined : 'var(--text-primary, #1A1A2E)' }}>{value}</p>
    </div>
  );
}

// ─── Task Detail Slide-Over ─────────────────────────────────────────────────
export default function TaskDetailPanel({ task, onClose, onRefresh, categories: propCategories, t, onToast }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [detail, setDetail] = useState(task);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [watcherSearch, setWatcherSearch] = useState('');
  const [watcherResults, setWatcherResults] = useState([]);
  const [showWatcherPicker, setShowWatcherPicker] = useState(false);
  const [watcherNames, setWatcherNames] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fileInputRef = useRef(null);
  // Accept flow state
  const [acceptForm, setAcceptForm] = useState({ due_date: '', priority: 'normal', category_id: '', stage_id: '' });
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineInput, setShowDeclineInput] = useState(false);
  const [acceptStages, setAcceptStages] = useState([]);

  const loadAttachments = useCallback(() => {
    if (!task) return;
    f(`/api/user-tasks/${task.id}/attachments`).then(r => r.ok ? r.json() : { attachments: [] }).then(d => setAttachments(d.attachments || [])).catch(() => {});
  }, [task]);

  // Load full task + users + attachments
  useEffect(() => {
    if (!task) return;
    f(`/api/user-tasks/${task.id}`).then(r => r.ok ? r.json() : null).then(d => { if (d?.task) setDetail(d.task); }).catch(() => {});
    f('/api/chatter/users/search?q=a').then(r => r.ok ? r.json() : { users: [] }).then(d => setUsers(d.users || [])).catch(() => {});
    loadAttachments();
  }, [task, loadAttachments]);

  // Resolve watcher names
  useEffect(() => {
    if (!detail?.watchers?.length || !users.length) { setWatcherNames([]); return; }
    const names = detail.watchers.map(wId => {
      const u = users.find(u => u.id === wId);
      return u ? { id: wId, name: u.name } : { id: wId, name: `User #${wId}` };
    });
    setWatcherNames(names);
  }, [detail?.watchers, users]);

  // Watcher search
  useEffect(() => {
    if (!showWatcherPicker) return;
    const ctl = new AbortController();
    const t = setTimeout(() => {
      f(`/api/chatter/users/search?q=${encodeURIComponent(watcherSearch || ' ')}`, { signal: ctl.signal })
        .then(r => r.json()).then(d => setWatcherResults(d.users || [])).catch(() => {});
    }, 200);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [showWatcherPicker, watcherSearch]);

  if (!task) return null;

  const prioLabels = PRIO_LABEL(t);
  const katLabels = KAT_LABEL(t);
  const statusLabels = STATUS_LABELS(t);
  const isOverdue = detail.due_date && new Date(detail.due_date) < new Date(todayISO()) && detail.status !== 'erledigt';
  const isDone = detail.status === 'erledigt';

  const startEdit = () => {
    setForm({
      title: detail.title || '',
      description: detail.description || '',
      due_date: detail.due_date ? String(detail.due_date).slice(0, 10) : '',
      estimated_minutes: detail.estimated_minutes || '',
      priority: detail.priority || 'normal',
      kategorie: detail.kategorie || '',
      assigned_to: detail.assigned_to || '',
      category_id: detail.category_id ? String(detail.category_id) : '',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const r = await f(`/api/user-tasks/${detail.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title?.trim() || detail.title,
          description: form.description || null,
          due_date: form.due_date || null,
          estimated_minutes: form.estimated_minutes ? parseInt(form.estimated_minutes, 10) : null,
          priority: form.priority,
          kategorie: form.kategorie || null,
          assigned_to: form.assigned_to ? parseInt(form.assigned_to, 10) : null,
          category_id: form.category_id ? parseInt(form.category_id, 10) : null,
        }),
      });
      if (r.ok) {
        const d = await r.json();
        setDetail(d.task);
        setEditing(false);
        onRefresh();
      }
    } catch { /* */ }
    setSaving(false);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const r = await f(`/api/user-tasks/${detail.id}/status`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (r.ok) {
        const d = await r.json();
        setDetail(d.task);
        onRefresh();
      }
    } catch { /* */ }
  };

  const addWatcher = async (userId) => {
    try {
      const r = await f(`/api/user-tasks/${detail.id}/watchers`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      if (r.ok) { const d = await r.json(); setDetail(d.task); }
    } catch { /* */ }
    setShowWatcherPicker(false);
    setWatcherSearch('');
  };

  const removeWatcher = async (userId) => {
    try {
      const r = await f(`/api/user-tasks/${detail.id}/watchers`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      if (r.ok) { const d = await r.json(); setDetail(d.task); }
    } catch { /* */ }
  };

  const handleFileUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      try { await f(`/api/user-tasks/${detail.id}/attachments`, { method: 'POST', body: fd }); } catch { /* */ }
    }
    setUploading(false);
    loadAttachments();
  };

  const handleDeleteAttachment = async (attId) => {
    try { await f(`/api/user-tasks/attachments/${attId}`, { method: 'DELETE' }); loadAttachments(); } catch { /* */ }
  };

  // Load stages when accept form category changes
  useEffect(() => {
    if (!acceptForm.category_id) { setAcceptStages([]); return; }
    f(`/api/user-tasks/stages?category_id=${acceptForm.category_id}`)
      .then(r => r.ok ? r.json() : { stages: [] })
      .then(d => setAcceptStages(d.stages || []))
      .catch(() => {});
  }, [acceptForm.category_id]);

  // Init accept form when detail loads
  useEffect(() => {
    if (detail && !detail.accepted_at) {
      setAcceptForm({
        due_date: detail.due_date ? String(detail.due_date).slice(0, 10) : '',
        priority: detail.priority || 'normal',
        category_id: detail.category_id ? String(detail.category_id) : '',
        stage_id: detail.stage_id ? String(detail.stage_id) : '',
      });
    }
  }, [detail?.id, detail?.accepted_at]);

  const handleAcceptFromOverlay = async () => {
    if (accepting) return;
    setAccepting(true);
    try {
      const r = await f(`/api/user-tasks/${detail.id}/accept`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          due_date: acceptForm.due_date || null,
          priority: acceptForm.priority,
          category_id: acceptForm.category_id ? Number(acceptForm.category_id) : null,
          stage_id: acceptForm.stage_id ? Number(acceptForm.stage_id) : null,
        }),
      });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.error || `HTTP ${r.status}`); }
      onToast?.({ message: t('meineAufgaben.accepted', 'Aufgabe angenommen'), type: 'success' });
      onRefresh();
      onClose();
    } catch (e) {
      onToast?.({ message: e.message || t('meineAufgaben.acceptError', 'Fehler'), type: 'error' });
    }
    setAccepting(false);
  };

  const handleDecline = async () => {
    if (declining) return;
    setDeclining(true);
    try {
      const r = await f(`/api/user-tasks/${detail.id}/decline`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: declineReason.trim() || null }),
      });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.error || `HTTP ${r.status}`); }
      onToast?.({ message: t('meineAufgaben.declined', 'Aufgabe abgelehnt'), type: 'success' });
      onRefresh();
      onClose();
    } catch (e) {
      onToast?.({ message: e.message || t('common.error', 'Fehler'), type: 'error' });
    }
    setDeclining(false);
  };

  const handleDeleteTask = () => setConfirmOpen(true);
  const executeDeleteTask = async () => {
    setConfirmOpen(false);
    setDeleting(true);
    try {
      const r = await f(`/api/user-tasks/${detail.id}`, { method: 'DELETE' });
      if (r.ok) { onRefresh(); onClose(); }
    } catch { /* */ }
    setDeleting(false);
  };

  const fileIcon = (mime) => {
    if (!mime) return File;
    if (mime.startsWith('image/')) return Image;
    if (mime.includes('pdf') || mime.includes('document') || mime.includes('text')) return FileText;
    return File;
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-[60]" onClick={onClose} />
      {/* Panel */}
      <div className="fixed inset-y-0 right-0 z-[70] w-full max-w-xl bg-[var(--bg-card,#fff)] border-l overflow-y-auto" style={{ borderColor: 'var(--border-card, #E5E7EB)', boxShadow: '-8px 0 30px rgba(0,0,0,0.12)' }}>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[var(--bg-card,#fff)] border-b px-5 py-4 flex items-center justify-between" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider flex-shrink-0 ${
              isDone ? 'bg-emerald-100 text-emerald-700' : isOverdue ? 'bg-red-100 text-red-700' : 'bg-blue-50 text-[#338edc]'
            }`}>
              {statusLabels[detail.status] || detail.status}
            </span>
            <h2 className="text-base font-bold truncate" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
              {detail.title}
            </h2>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={handleDeleteTask} disabled={deleting}
              className="p-1.5 rounded-lg hover:bg-red-50 text-[var(--text-muted,#9CA3AF)] hover:text-red-500 transition-colors"
              title={t('meineAufgaben.delete', 'Loeschen')}>
              <Trash2 className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {/* ── Info Section (view / edit) ── */}
          {!editing ? (
            <div className="space-y-4">
              {/* Description */}
              {detail.description && (
                <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                  {detail.description}
                </p>
              )}

              {/* Meta Grid */}
              <div className="grid grid-cols-2 gap-3">
                <MetaItem label={t('meineAufgaben.priority', 'Prioritaet')} value={prioLabels[detail.priority] || detail.priority} />
                <MetaItem label={t('meineAufgaben.dueDate', 'Faellig am')} value={detail.due_date ? new Date(detail.due_date + (String(detail.due_date).length === 10 ? 'T00:00:00' : '')).toLocaleDateString('de-AT') : '—'} danger={isOverdue} />
                {detail.kategorie && <MetaItem label={t('meineAufgaben.category', 'Kategorie')} value={katLabels[detail.kategorie] || detail.kategorie} />}
                {detail.category_id && (() => { const cat = (propCategories || []).find(c => c.id === detail.category_id); return cat ? <MetaItem label={t('meineAufgaben.taskCategory', 'Board-Kategorie')} value={<span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: cat.color || '#338EDC' }} />{cat.name}</span>} /> : null; })()}
                {detail.estimated_minutes && <MetaItem label={t('meineAufgaben.duration', 'Dauer')} value={`${detail.estimated_minutes} min`} />}
              </div>

              {/* Creator + Assignee */}
              <div className="flex items-center gap-4">
                {detail.creator_name && (
                  <div className="flex items-center gap-2">
                    <Avatar name={detail.creator_name} size={24} />
                    <div>
                      <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{t('meineAufgaben.creator', 'Ersteller')}</p>
                      <p className="text-xs font-medium" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{detail.creator_name}</p>
                    </div>
                  </div>
                )}
                {detail.assigned_name && (
                  <div className="flex items-center gap-2">
                    <Avatar name={detail.assigned_name} size={24} />
                    <div>
                      <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{t('meineAufgaben.assignee', 'Bearbeiter')}</p>
                      <p className="text-xs font-medium" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{detail.assigned_name}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Entity Link — enhanced for case type */}
              {detail.entity_type && detail.entity_id && ENTITY_PATH[detail.entity_type] && (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border" style={{ borderColor: 'var(--border-card, #E5E7EB)', background: 'var(--bg-inset, #F8F9FC)' }}>
                  <LinkIcon className="w-4 h-4 shrink-0" style={{ color: '#338edc' }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-medium" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                      {t('meineAufgaben.linkedWith', 'Verknuepft mit')}
                    </div>
                    <button
                      onClick={() => navigate(ENTITY_PATH[detail.entity_type](detail.entity_id))}
                      className="text-sm font-medium hover:underline truncate block text-left"
                      style={{ color: '#338edc' }}
                    >
                      {detail.entity_label || `${detail.entity_type} #${detail.entity_id}`}
                    </button>
                  </div>
                  {detail.entity_type === 'case' && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0" style={{ background: '#FEE2E2', color: '#DC2626' }}>CASE</span>
                  )}
                  <button
                    onClick={() => navigate(ENTITY_PATH[detail.entity_type](detail.entity_id))}
                    className="px-2.5 py-1 rounded-lg text-[11px] font-semibold shrink-0 hover:bg-blue-50 transition-colors"
                    style={{ color: '#338edc', border: '1px solid #338edc' }}
                  >
                    {detail.entity_type === 'case' ? t('meineAufgaben.openCase', 'Case oeffnen') : t('meineAufgaben.openLink', 'Oeffnen')}
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await f(`/api/user-tasks/${detail.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ entity_type: null, entity_id: null, entity_label: null }) });
                        setDetail(d => ({ ...d, entity_type: null, entity_id: null, entity_label: null }));
                        onRefresh();
                      } catch {}
                    }}
                    className="p-1 rounded-lg hover:bg-red-50 text-[var(--text-muted)] hover:text-red-500 transition-colors shrink-0"
                    title={t('meineAufgaben.unlinkEntity', 'Verknuepfung entfernen')}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}

              {/* Accept Flow (Inbox tasks) */}
              {!detail.accepted_at && detail.source !== 'manual' && (
                <div className="p-4 rounded-lg border space-y-3" style={{ borderColor: '#338edc', background: 'var(--bg-inset, #F8F9FC)' }}>
                  <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: '#338edc' }}>
                    {t('meineAufgaben.acceptConfig', 'Aufgabe konfigurieren')}
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>{t('meineAufgaben.dueDate', 'Faellig am')}</label>
                      <input type="date" value={acceptForm.due_date} onChange={e => setAcceptForm(s => ({ ...s, due_date: e.target.value }))}
                        className="w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: 'var(--border-input)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary)' }} />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>{t('meineAufgaben.priority', 'Prioritaet')}</label>
                      <select value={acceptForm.priority} onChange={e => setAcceptForm(s => ({ ...s, priority: e.target.value }))}
                        className="w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: 'var(--border-input)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary)' }}>
                        {Object.entries(prioLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>{t('meineAufgaben.category', 'Kategorie')}</label>
                      <select value={acceptForm.category_id} onChange={e => setAcceptForm(s => ({ ...s, category_id: e.target.value, stage_id: '' }))}
                        className="w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: 'var(--border-input)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary)' }}>
                        <option value="">—</option>
                        {(propCategories || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>{t('meineAufgaben.stage', 'Stage')}</label>
                      <select value={acceptForm.stage_id} onChange={e => setAcceptForm(s => ({ ...s, stage_id: e.target.value }))}
                        className="w-full px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: 'var(--border-input)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary)' }}>
                        <option value="">— Backlog —</option>
                        {acceptStages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <button onClick={handleAcceptFromOverlay} disabled={accepting}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                      style={{ background: '#338edc' }}>
                      <CheckCircle2 className="w-4 h-4" />
                      {accepting ? '...' : t('meineAufgaben.accept', 'Annehmen')}
                    </button>
                    {!showDeclineInput ? (
                      <button onClick={() => setShowDeclineInput(true)}
                        className="px-3 py-2 rounded-lg text-sm font-medium border border-red-300 text-red-600 hover:bg-red-50 transition-colors">
                        {t('meineAufgaben.decline', 'Ablehnen')}
                      </button>
                    ) : (
                      <div className="flex items-center gap-2 flex-1">
                        <input value={declineReason} onChange={e => setDeclineReason(e.target.value)}
                          placeholder={t('meineAufgaben.declineReason', 'Begruendung (optional)')}
                          className="flex-1 px-2.5 py-1.5 rounded-lg border text-sm" style={{ borderColor: 'var(--border-input)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary)' }} />
                        <button onClick={handleDecline} disabled={declining}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-red-500 hover:bg-red-600 disabled:opacity-60">
                          {declining ? '...' : t('meineAufgaben.declineConfirm', 'Ablehnen')}
                        </button>
                        <button onClick={() => { setShowDeclineInput(false); setDeclineReason(''); }}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-[var(--text-muted)]">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons (for accepted tasks) */}
              {(detail.accepted_at || detail.source === 'manual') && (
              <div className="flex items-center gap-2 flex-wrap">
                {!isDone ? (
                  <button
                    onClick={() => handleStatusChange('erledigt')}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                    style={{ background: '#22C55E' }}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {t('meineAufgaben.markDone', 'Erledigt')}
                  </button>
                ) : (
                  <button
                    onClick={() => handleStatusChange('offen')}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border"
                    style={{ borderColor: 'var(--border-default, #D1D5DB)', color: 'var(--text-secondary, #4B5563)' }}
                  >
                    {t('meineAufgaben.markOpen', 'Wieder oeffnen')}
                  </button>
                )}
                <button
                  onClick={startEdit}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border"
                  style={{ borderColor: 'var(--border-default, #D1D5DB)', color: 'var(--text-secondary, #4B5563)' }}
                >
                  <Edit3 className="w-3.5 h-3.5" />
                  {t('meineAufgaben.editTask', 'Bearbeiten')}
                </button>
              </div>
              )}
            </div>
          ) : (
            /* ── Edit Form ── */
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>{t('meineAufgaben.title', 'Titel')} *</label>
                <input type="text" value={form.title} onChange={e => setForm(s => ({ ...s, title: e.target.value }))} autoFocus
                  className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }} />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>{t('meineAufgaben.description', 'Beschreibung')}</label>
                <textarea value={form.description} onChange={e => setForm(s => ({ ...s, description: e.target.value }))} rows={2}
                  className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>{t('meineAufgaben.dueDate', 'Faellig am')}</label>
                  <input type="date" value={form.due_date} onChange={e => setForm(s => ({ ...s, due_date: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }} />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>{t('meineAufgaben.duration', 'Dauer (min)')}</label>
                  <input type="number" min="0" value={form.estimated_minutes} onChange={e => setForm(s => ({ ...s, estimated_minutes: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>{t('meineAufgaben.priority', 'Prioritaet')}</label>
                  <select value={form.priority} onChange={e => setForm(s => ({ ...s, priority: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }}>
                    {Object.entries(prioLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>{t('meineAufgaben.assignTo', 'Zuweisen an')}</label>
                  <select value={form.assigned_to} onChange={e => setForm(s => ({ ...s, assigned_to: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }}>
                    <option value="">{t('meineAufgaben.assignSelf', 'Mir selbst')}</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              </div>
              {(propCategories || []).length > 0 && (
                <div>
                  <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>{t('meineAufgaben.taskCategory', 'Board-Kategorie')}</label>
                  <select value={form.category_id} onChange={e => setForm(s => ({ ...s, category_id: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }}>
                    <option value="">—</option>
                    {(propCategories || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-2">
                <button onClick={() => setEditing(false)} className="px-4 py-2 rounded-lg text-sm border" style={{ borderColor: 'var(--border-default, #D1D5DB)', color: 'var(--text-secondary, #4B5563)' }}>
                  {t('meineAufgaben.cancel', 'Abbrechen')}
                </button>
                <button onClick={saveEdit} disabled={saving} className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ background: '#338edc' }}>
                  {saving ? '...' : t('meineAufgaben.save', 'Speichern')}
                </button>
              </div>
            </div>
          )}

          {/* ── Watchers ── */}
          <div className="border-t pt-4" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-3.5 h-3.5" style={{ color: 'var(--text-tertiary, #6B7280)' }} />
              <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                {t('meineAufgaben.watchers', 'Beobachter')} ({watcherNames.length})
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {watcherNames.map(w => (
                <button
                  key={w.id}
                  onClick={() => removeWatcher(w.id)}
                  title={t('meineAufgaben.removeWatcher', 'Beobachter entfernen')}
                  className="flex items-center gap-1.5 px-1.5 py-1 rounded-full border hover:border-red-500/50 transition-colors"
                  style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-inset, #F3F4F6)' }}
                >
                  <Avatar name={w.name} size={18} />
                  <span className="text-[11px] max-w-[80px] truncate" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{w.name}</span>
                  <X className="w-3 h-3" style={{ color: 'var(--text-muted, #9CA3AF)' }} />
                </button>
              ))}
              <div className="relative">
                <button
                  onClick={() => setShowWatcherPicker(s => !s)}
                  className="flex items-center gap-1 px-2 py-1 rounded-full border border-dashed text-[11px] hover:border-[#338edc] hover:text-[#338edc] transition-colors"
                  style={{ borderColor: 'var(--border-default, #D1D5DB)', color: 'var(--text-tertiary, #6B7280)' }}
                >
                  <Plus className="w-3 h-3" />
                  {t('meineAufgaben.addWatcher', 'Hinzufuegen')}
                </button>
                {showWatcherPicker && (
                  <div className="absolute z-50 top-full mt-1 left-0 w-56 rounded-lg border bg-[var(--bg-card,#fff)] shadow-sm p-2" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
                    <input type="text" value={watcherSearch} onChange={e => setWatcherSearch(e.target.value)}
                      placeholder={t('meineAufgaben.searchUser', 'Suchen...')} autoFocus
                      className="w-full px-2 py-1.5 text-xs rounded-lg border mb-2" style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }} />
                    <div className="max-h-40 overflow-y-auto">
                      {watcherResults.filter(u => !(detail.watchers || []).includes(u.id)).map(u => (
                        <button key={u.id} onClick={() => addWatcher(u.id)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-xs hover:bg-[var(--bg-inset,#F8F9FC)]">
                          <Avatar name={u.name} size={20} />
                          <span style={{ color: 'var(--text-primary, #1A1A2E)' }}>{u.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Attachments ── */}
          <div className="border-t pt-4" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
            <div className="flex items-center gap-2 mb-2">
              <Paperclip className="w-3.5 h-3.5" style={{ color: 'var(--text-tertiary, #6B7280)' }} />
              <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                {t('meineAufgaben.attachments', 'Dateien')} ({attachments.length})
              </span>
            </div>
            {attachments.length > 0 && (
              <div className="space-y-1.5 mb-2">
                {attachments.map(att => {
                  const FIcon = fileIcon(att.mime_type);
                  return (
                    <div key={att.id} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border" style={{ borderColor: 'var(--border-default, #D1D5DB)' }}>
                      <FIcon className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-tertiary, #6B7280)' }} />
                      <span className="flex-1 text-xs truncate" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{att.file_name}</span>
                      {att.file_size && <span className="text-[10px] flex-shrink-0" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{att.file_size > 1048576 ? `${(att.file_size / 1048576).toFixed(1)} MB` : `${Math.round(att.file_size / 1024)} KB`}</span>}
                      <a href={`${API}/api/user-tasks/attachments/${att.id}/download`} target="_blank" rel="noreferrer"
                        className="p-1 rounded hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary)] hover:text-[#338edc] transition-colors" title={t('meineAufgaben.download', 'Herunterladen')}>
                        <Download className="w-3.5 h-3.5" />
                      </a>
                      <button onClick={() => handleDeleteAttachment(att.id)}
                        className="p-1 rounded hover:bg-red-50 text-[var(--text-muted,#9CA3AF)] hover:text-red-500 transition-colors" title={t('meineAufgaben.delete', 'Loeschen')}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={e => { handleFileUpload(e.target.files); e.target.value = ''; }} />
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed text-[11px] font-medium hover:border-[#338edc] hover:text-[#338edc] transition-colors disabled:opacity-50"
              style={{ borderColor: 'var(--border-default, #D1D5DB)', color: 'var(--text-tertiary, #6B7280)' }}>
              <Plus className="w-3 h-3" />
              {uploading ? t('meineAufgaben.uploading', 'Hochladen...') : t('meineAufgaben.addFile', 'Datei hinzufuegen')}
            </button>
          </div>

          {/* ── Chatter ── */}
          <div className="border-t pt-4" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
            <UniversalChatter entityType="task" entityId={detail.id} />
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        onConfirm={executeDeleteTask}
        onCancel={() => setConfirmOpen(false)}
        title={t('meineAufgaben.deleteTitle', 'Aufgabe loeschen')}
        message={t('meineAufgaben.deleteMessage', 'Diese Aufgabe wird unwiderruflich geloescht.')}
        confirmLabel={t('meineAufgaben.delete', 'Loeschen')}
        cancelLabel={t('meineAufgaben.cancel', 'Abbrechen')}
        variant="danger"
        zIndex={80}
      />
    </>
  );
}
